import { UserRole } from '@/constants/room'
import axiosInstance from '@/api/apiClient'
import { getErrorMessage, getStructuredError, type StructuredApiError } from '@/lib/utils'
import { User } from '@/redux/types/user'
import { TimerMode } from '@/components/settings/timer-configuration/types'
import {
  DashboardKPIs,
  GameSummaryResponse,
  ResourceEnforcementMode,
} from '@/redux/types/room'
import {
  AdvisorMessageDistributionResponse,
  UsageStatsAggregateResponse,
  UsageStatsPlayerResponse,
} from '@/redux/types/usageStats'

export const createNewRoom = async (
  user: User,
  roomName: string,
  timerMode: TimerMode,
  /** Creates a standalone Charter Class instead of a game room. The backend
   *  derives the charter flags and skips all game scaffolding. */
  isCharterOnly = false
) => {
  try {
    const response = await axiosInstance.post('/oge/create-room/', {
      id: user.id || null,
      roomname: roomName,
      game_mode: timerMode,
      is_charter_only: isCharterOnly,
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const duplicateRoomAPI = async (
  roomId: string,
  withAssignments: boolean
) => {
  try {
    const response = await axiosInstance.post(
      `/oge/duplicate-room/${roomId}/`,
      {
        withAssignments,
      },
      {
        timeout: 120000, // 120 seconds timeout for room duplication
      }
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/** Which kind of room to list. The backend defaults to `game`, so omitting
 *  this keeps Charter Classes out of the game dashboard. */
export type RoomListType = 'game' | 'charter' | 'all'

export const fetchAllRooms = async (
  sortBy?: string,
  searchQuery?: string,
  type?: RoomListType
) => {
  try {
    const params: { sort_by?: string; search?: string; type?: RoomListType } = {}
    if (sortBy) params.sort_by = sortBy
    if (searchQuery) params.search = searchQuery
    if (type) params.type = type
    const response = await axiosInstance.get('/oge/rooms/', { params })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const fetchDashboardKPIsAPI = async (): Promise<DashboardKPIs> => {
  try {
    const response = await axiosInstance.get('/oge/rooms/dashboard-stats/')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const deleteRoomAPI = async (roomId: string) => {
  try {
    await axiosInstance.delete(`/oge/rooms/${roomId}/`)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const getRoomDetailsAPI = async (roomId: string) => {
  try {
    const response = await axiosInstance.get(`/oge/rooms/${roomId}/`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export interface GenerateEventMediaParams {
  event_id: number
  prompt: string
  media_type?: 'image'
  scenario_title?: string
  event_title?: string
  event_text?: string
}

export interface GenerateEventMediaResponse {
  image_b64?: string
  media_type?: string
}

export interface UploadEventMediaResponse {
  id: number
  type: string
  url: string
}

export const generateEventMediaAPI = async (
  roomId: string,
  params: GenerateEventMediaParams
): Promise<GenerateEventMediaResponse> => {
  try {
    const response = await axiosInstance.post<GenerateEventMediaResponse>(
      `/oge/rooms/${roomId}/events/generate-media/`,
      {
        event_id: params.event_id,
        prompt: params.prompt,
        media_type: params.media_type ?? 'image',
        ...(params.scenario_title !== undefined && {
          scenario_title: params.scenario_title,
        }),
        ...(params.event_title !== undefined && {
          event_title: params.event_title,
        }),
        ...(params.event_text !== undefined && {
          event_text: params.event_text,
        }),
      }
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const uploadEventMediaAPI = async (
  roomId: string,
  eventId: number,
  file: File
): Promise<UploadEventMediaResponse> => {
  try {
    const formData = new FormData()
    formData.append('event_id', String(eventId))
    formData.append('file', file)
    const response = await axiosInstance.post<UploadEventMediaResponse>(
      `/oge/rooms/${roomId}/events/upload-media/`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const deleteEventMediaAPI = async (
  roomId: string,
  mediaId: number
): Promise<void> => {
  try {
    await axiosInstance.delete(
      `/oge/rooms/${roomId}/events/media/${mediaId}/`
    )
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export interface GenerateTTQDocumentParams {
  event_id: number
  quill_type: string
  markdown_text: string
}

export interface GenerateTTQDocumentResponse {
  id: number
  type: string
  url: string
  caption?: string
}

export interface QuillType {
  name: string
  description: string
}

export const fetchQuillTypesAPI = async (roomId: string): Promise<QuillType[]> => {
  try {
    const response = await axiosInstance.get<QuillType[]>(
      `/oge/rooms/${roomId}/events/quill-types/`
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export class TTQApiError extends Error {
  code: string
  userMessage: string
  constructor(structured: StructuredApiError) {
    super(structured.message)
    this.code = structured.code
    this.userMessage = structured.message
  }
}

export const generateTTQDocumentAPI = async (
  roomId: string,
  params: GenerateTTQDocumentParams
): Promise<GenerateTTQDocumentResponse> => {
  try {
    const response = await axiosInstance.post<GenerateTTQDocumentResponse>(
      `/oge/rooms/${roomId}/events/generate-ttq-document/`,
      {
        event_id: params.event_id,
        quill_type: params.quill_type,
        markdown_text: params.markdown_text,
      },
      { timeout: 60000 }
    )
    return response.data
  } catch (error) {
    throw new TTQApiError(getStructuredError(error))
  }
}

export const sendInviteAPI = async (
  roomId: string,
  emails: string[],
  userRole: UserRole,
  sendEmail?: boolean,
  teamAssignments?: Record<string, number>,
  requirePasswordConfiguration?: boolean
) => {
  try {
    const payload: {
      invitees: string[]
      room_id: string
      user_role: UserRole
      send_email?: boolean
      team_assignments?: Record<string, number>
      require_password_configuration?: boolean
    } = {
      invitees: emails,
      room_id: roomId,
      user_role: userRole,
      send_email: sendEmail,
    }
    if (teamAssignments) {
      payload.team_assignments = teamAssignments
    }
    if (requirePasswordConfiguration === false) {
      payload.require_password_configuration = false
    }
    const response = await axiosInstance.post('/oge/send-invites/', payload)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const resendInviteAPI = async (
  roomId: string,
  emails: string[],
  userRole: UserRole,
  playerRoles?: Record<string, string>,
  customMessage?: string
) => {
  try {
    const response = await axiosInstance.post('/oge/resend-invites/', {
      invitees: emails,
      room_id: roomId,
      user_role: userRole,
      player_roles: playerRoles || {},
      custom_message: customMessage,
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const getRoomMembersAPI = async (roomId: string) => {
  try {
    const response = await axiosInstance.get(`/oge/members/${roomId}/`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const updateRoomDetailsAPI = async (
  roomId: string,
  payload: {
    scenario_title?: string
    events?: Array<{
      id?: number
      title: string
      text: string
      event_datetime: Date
      is_released: boolean
      media_ids?: number[]
      affects_all_players?: boolean
      assigned_player_ids?: number[]
      affected_team_ids?: number[]
    }>
    roomname?: string
    status?: number
    start_time?: string
    ends_in?: number
    gameroom_llm?: string
    auto_approve?: boolean
    enable_chat_history_summarization?: boolean
    is_locked?: boolean
    is_simulating?: boolean
    resource_limits_enabled?: boolean
    resource_enforcement_mode?: ResourceEnforcementMode
    default_currency_amount?: number | null
    default_message_limit?: number | null
    default_token_limit?: number | null
    openai_api_key?: string | null
    claude_api_key?: string | null
  }
) => {
  try {
    const response = await axiosInstance.put(`/oge/rooms/${roomId}/`, payload)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const removePlayerAPI = async (roomId: string, playerId: string) => {
  try {
    const response = await axiosInstance.delete(
      `/oge/rooms/${roomId}/players/${playerId}/remove/`
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const bulkRemoveStudentsAPI = async (roomId: string) => {
  try {
    const response = await axiosInstance.delete(
      `/oge/rooms/${roomId}/players/bulk-remove/`
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const resetRoomAPI = async (roomId: string) => {
  try {
    const response = await axiosInstance.post(`/oge/rooms/${roomId}/reset/`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const markTeamOrientationShownAPI = async (roomId: string) => {
  try {
    const response = await axiosInstance.post(
      `/oge/rooms/${roomId}/team-orientation/shown/`
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const getCommunicationSettingsAPI = async (roomId: string) => {
  try {
    const response = await axiosInstance.get(
      `/oge/rooms/${roomId}/communications/`
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export interface CoProfessor {
  id: number
  email: string
  full_name: string
}

export const getCoProfessorsAPI = async (roomId: string): Promise<CoProfessor[]> => {
  try {
    const response = await axiosInstance.get(`/oge/rooms/${roomId}/co-professors/`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const leaveProfessorGameAPI = async (
  roomId: string,
  newOwnerId?: number
) => {
  try {
    const response = await axiosInstance.delete(`/oge/rooms/${roomId}/leave/`, {
      data: newOwnerId !== undefined ? { new_owner_id: newOwnerId } : undefined,
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const updateCommunicationSettingsAPI = async (
  roomId: string,
  invitationMessageTemplate: string
) => {
  try {
    const response = await axiosInstance.put(
      `/oge/rooms/${roomId}/communications/`,
      {
        invitation_message_template: invitationMessageTemplate,
      }
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const getGameSummary = async (
  roomId: string
): Promise<GameSummaryResponse> => {
  try {
    const response = await axiosInstance.get(
      `/oge/rooms/${roomId}/game-summary/`
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const getUsageStatsAggregate = async (
  roomId: string
): Promise<UsageStatsAggregateResponse> => {
  try {
    const response = await axiosInstance.get(
      `/oge/rooms/${roomId}/usage-stats/aggregate/`
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const getMyUsageStats = async (
  roomId: string
): Promise<UsageStatsPlayerResponse> => {
  try {
    const response = await axiosInstance.get(`/oge/rooms/${roomId}/usage-stats/me/`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export interface ValidateLLMApiKeysResult {
  valid: boolean | null
  error?: string
}

export interface ValidateLLMApiKeysResponse {
  openai?: ValidateLLMApiKeysResult
  claude?: ValidateLLMApiKeysResult
}

export const validateLLMApiKeysAPI = async (
  roomId: string,
  keys: { openai_api_key?: string; claude_api_key?: string }
): Promise<ValidateLLMApiKeysResponse> => {
  try {
    const response = await axiosInstance.post<ValidateLLMApiKeysResponse>(
      `/oge/rooms/${roomId}/validate-llm-keys/`,
      keys,
      { timeout: 15000 }
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const exportUsageStats = async (
  roomId: string
): Promise<Blob> => {
  try {
    const response = await axiosInstance.get(
      `/oge/rooms/${roomId}/usage-stats/export/`,
      {
        responseType: 'blob',
      }
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export interface ExportGameOptions {
  include_players?: boolean
  include_teams?: boolean
  include_ai_player_threads?: boolean
  include_events?: boolean
  include_rounds?: boolean
  include_communications?: boolean
  include_resource_limits?: boolean
  include_world_setup?: boolean
  include_advisors?: boolean
  include_team_assignments?: boolean
  include_mcp_config?: boolean
  include_team_chats?: boolean
  include_conference?: boolean
  include_advisor_chats?: boolean
  include_adjudication_bot?: boolean
  include_adjudication_chats?: boolean
  include_ai_players?: boolean
  include_decisions?: boolean
  include_team_mode_state?: boolean
}

export const initiateGameExportAPI = async (
  roomId: string,
  options: ExportGameOptions
): Promise<{ export_token: string; message: string }> => {
  try {
    const response = await axiosInstance.post(
      `/oge/rooms/${roomId}/export/`,
      options
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const downloadGameExportAPI = async (
  roomId: string,
  exportToken: string
): Promise<Blob> => {
  try {
    const response = await axiosInstance.get(
      `/oge/rooms/${roomId}/export/${exportToken}/download/`,
      { responseType: 'blob' }
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const initiateAllAdvisorsExportAPI = async (
  roomId: string,
  withAssignments = true
): Promise<{ export_token: string; message: string }> => {
  try {
    const response = await axiosInstance.post(
      `/oge/rooms/${roomId}/advisors/export/`,
      { with_assignments: withAssignments }
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export const getAdvisorMessageDistribution = async (
  roomId: string
): Promise<AdvisorMessageDistributionResponse> => {
  try {
    const response = await axiosInstance.get(
      `/oge/rooms/${roomId}/advisor-message-distribution/`
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}
